// import { Locale } from './interfaces';

import { cookies } from './cookies';

// Offusco i dati in base64 per evitare che siano immediatamente leggibili dall'utente medio, per ora non gestisco encrypt avanzati
const obfuscateValue = (value: string): string => {
  return window.btoa(encodeURIComponent(value));
};

const deobfuscateValue = (value: string): string => {
  return decodeURIComponent(window.atob(value));
};

/**
 * Funzioni interne
 */

const getItem = (key: string, persistent = false, obfuscate = false): string | undefined => {
  const prefixedKey = cookies.getPrefixedKey(key);
  const item = persistent ? window.localStorage.getItem(prefixedKey) : window.sessionStorage.getItem(prefixedKey);
  if (item === null) return undefined;
  return obfuscate ? deobfuscateValue(item) : item;
};
const getItemNotUndefined = (key: string, persistent = false, obfuscate = false): string => {
  const item = getItem(key, persistent, obfuscate);
  if (item === undefined) throw new Error(`${key} not found in storage`);
  return item;
};
const setItem = (key: string, value: string, persist = false, obfuscate = false) => {
  const prefixedKey = cookies.getPrefixedKey(key);
  const storedValue = obfuscate ? obfuscateValue(value) : value;
  if (persist) {
    window.localStorage.setItem(prefixedKey, storedValue);
  } else {
    window.sessionStorage.setItem(prefixedKey, storedValue);
  }
};
const removeItem = (key: string, persistent = false) => {
  const prefixedKey = cookies.getPrefixedKey(key);
  if (persistent) {
    window.localStorage.removeItem(prefixedKey);
  } else {
    window.sessionStorage.removeItem(prefixedKey);
  }
};

/**
 * String
 */

const getString = (key: string, persistent?: boolean, obfuscate?: boolean): string | undefined => {
  return getItem(key, persistent, obfuscate);
};

const getStringNotUndefined = (key: string, persistent?: boolean, obfuscate?: boolean): string => {
  return getItemNotUndefined(key, persistent, obfuscate);
};

const setString = (key: string, value: string, persist?: boolean, obfuscate?: boolean) => {
  return setItem(key, value, persist, obfuscate);
};

/**
 * Number
 */

const getNumber = (key: string, persistent?: boolean, obfuscate?: boolean): number | undefined => {
  const value = getItem(key, persistent, obfuscate);
  if (value) {
    return parseInt(value);
  } else {
    return undefined;
  }
};

const getNumberNotUndefined = (key: string, persistent?: boolean, obfuscate?: boolean): number => {
  const value = getItemNotUndefined(key, persistent, obfuscate);
  return parseInt(value);
};

const setNumber = (key: string, value: number, persist?: boolean, obfuscate?: boolean) => {
  return setItem(key, value.toString(), persist, obfuscate);
};

/**
 * Enum
 */

const getEnum = <T extends string>(key: string, persistent?: boolean, obfuscate?: boolean): T | undefined => {
  const value = getItem(key, persistent, obfuscate);
  if (value) {
    return value as T;
  } else {
    return undefined;
  }
};

const getEnumNotUndefined = <T extends string>(key: string, persistent?: boolean, obfuscate?: boolean): T => {
  const value = getItemNotUndefined(key, persistent, obfuscate);
  return value as T;
};

const setEnum = <T extends string>(key: string, value: T, persist?: boolean, obfuscate?: boolean) => {
  return setItem(key, value, persist, obfuscate);
};

/**
 * Boolean
 */

const getBoolean = (key: string, persistent?: boolean, obfuscate?: boolean): boolean => {
  const value = getItem(key, persistent, obfuscate);
  if (value && JSON.parse(value)) {
    return true;
  } else {
    return false;
  }
};

const setBoolean = (key: string, value: boolean, persist?: boolean, obfuscate?: boolean) => {
  return setItem(key, JSON.stringify(value), persist, obfuscate);
};

/**
 * Object
 */

const getObject = <T>(key: string, persistent?: boolean, obfuscate?: boolean): T | undefined => {
  const value = getItem(key, persistent, obfuscate);
  if (value) {
    return JSON.parse(value);
  } else {
    return undefined;
  }
};

const getObjectNotUndefined = <T>(key: string, persistent?: boolean, obfuscate?: boolean): T => {
  const value = getItemNotUndefined(key, persistent, obfuscate);
  return JSON.parse(value);
};

const setObject = <T>(key: string, value: T, persist?: boolean, obfuscate?: boolean) => {
  return setItem(key, JSON.stringify(value), persist, obfuscate);
};

/**
 * Da qui iniziano i metodi dedicati da esportare
 */

export const storage = {
  getBoolean,
  getEnum,
  getEnumNotUndefined,
  getNumber,
  getNumberNotUndefined,
  getObject,
  getObjectNotUndefined,
  getString,
  getStringNotUndefined,
  removeItem,
  setBoolean,
  setEnum,
  setNumber,
  setObject,
  setString,
};
